import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { teamAPI } from '../services/api';
import { ArrowLeft, Plus, Trash2, Shield, ShieldCheck, UserCog, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin: { label: 'Administrator', desc: 'Voller Zugriff auf alle Bereiche' },
  editor: { label: 'Editor', desc: 'Kann Projekte und Downloads verwalten' }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

export const AdminTeam = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'editor'
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await teamAPI.list();
      if (res?.success) setTeam(res.team || []);
    } catch (e) {
      toast.error(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm({ firstName: '', lastName: '', email: '', password: '', role: 'editor' });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    try {
      setSubmitting(true);
      await teamAPI.create(form);
      toast.success(`${ROLE_LABELS[form.role].label} "${form.firstName} ${form.lastName}" angelegt`);
      setIsCreateOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.message || 'Konnte nicht angelegt werden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member) => {
    if (member.id === user.id) {
      toast.error('Sie können sich nicht selbst löschen');
      return;
    }
    if (!window.confirm(`Konto "${member.firstName} ${member.lastName}" wirklich löschen?`)) return;
    try {
      await teamAPI.remove(member.id);
      toast.success('Konto gelöscht');
      load();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-[#C4A55A] hover:text-[#D4B56A] mb-2"
              data-testid="admin-team-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <UserCog className="h-9 w-9 text-[#C4A55A]" />
              Team verwalten
            </h1>
            <p className="text-gray-400 mt-1">
              Administratoren und Editoren mit Backend-Zugriff
            </p>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={(open) => {
            setIsCreateOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="admin-team-create-open-btn"
                className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Neues Konto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">Neues Team-Konto</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Legt einen Admin- oder Editor-Zugang an
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Vorname</Label>
                    <Input
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="admin-team-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nachname</Label>
                    <Input
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="admin-team-lastname-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">E-Mail</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    required
                    data-testid="admin-team-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Passwort (min. 8 Zeichen)</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    required
                    minLength={8}
                    data-testid="admin-team-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Rolle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      data-testid="admin-team-role-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#C4A55A]/30 text-white">
                      <SelectItem value="editor">Editor (Projekte + Downloads)</SelectItem>
                      <SelectItem value="admin">Administrator (Vollzugriff)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {ROLE_LABELS[form.role]?.desc}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                  data-testid="admin-team-create-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lege an...</>
                  ) : 'Konto anlegen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-gray-400" data-testid="admin-team-loading">Lädt...</div>
        ) : team.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardContent className="py-12 text-center text-gray-400" data-testid="admin-team-empty">
              Keine Team-Mitglieder vorhanden.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-team-list">
            {team.map((m) => {
              const isSelf = m.id === user?.id;
              const isAdmin = m.role === 'admin';
              const Icon = isAdmin ? ShieldCheck : Shield;
              return (
                <Card
                  key={m.id}
                  data-testid={`admin-team-card-${m.id}`}
                  className={`bg-[#1a1a1a] ${
                    isAdmin
                      ? 'border-[#C4A55A]/40 shadow-lg shadow-[#C4A55A]/10'
                      : 'border-[#C4A55A]/20'
                  }`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-lg shrink-0 ${
                        isAdmin
                          ? 'bg-[#C4A55A]/15 border border-[#C4A55A]/40'
                          : 'bg-emerald-500/10 border border-emerald-500/30'
                      }`}>
                        <Icon className={`h-5 w-5 ${isAdmin ? 'text-[#C4A55A]' : 'text-emerald-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-white text-base flex items-center gap-2 flex-wrap">
                          {m.firstName} {m.lastName}
                          {isSelf && (
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide rounded bg-[#C4A55A]/20 text-[#C4A55A]">
                              Sie
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription className="text-gray-400 truncate" title={m.email}>
                          {m.email}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        isAdmin
                          ? 'bg-[#C4A55A]/15 text-[#C4A55A]'
                          : 'bg-emerald-500/15 text-emerald-400'
                      }`}>
                        {ROLE_LABELS[m.role]?.label || m.role}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{fmtDate(m.createdAt)}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isSelf}
                          className="border-red-500/30 text-red-500 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          onClick={() => handleDelete(m)}
                          data-testid={`admin-team-delete-btn-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
